import Table from 'ui/Table';

import { useSankeyRenderContext } from '../../contexts/sankeyRenderContext';
import { useAmountFormatter } from '../../diagram/formatting';
import ToolCard from '../ToolCard';
import ImbalanceValue from './ImbalanceValue';

const NodeImbalances: React.FC = () => {
  const { maxDecimalPlaces, nodes } = useSankeyRenderContext();

  const formatAmount = useAmountFormatter();

  // Given maxDecimalPlaces, we can derive the smallest important
  // difference, defined as smallest-input-decimal/10; this lets us work
  // around various binary/decimal math issues.
  const epsilonDifference = 10 ** (-maxDecimalPlaces - 1);

  // Filter out nodes with a difference smaller than epsilonDifference.
  const diffNodes = nodes?.filter((node) => {
    const totalIn = node.totalIn ?? NaN;
    const totalOut = node.totalOut ?? NaN;
    const hasSignificantDiff = Math.abs(totalIn - totalOut) > epsilonDifference;
    return totalIn > 0 && totalOut > 0 && hasSignificantDiff;
  });

  // Render a simple info if there are no imbalances.
  if (!diffNodes?.length) {
    return (
      <ToolCard
        title="All nodes are balanced 🪄"
        tooltip="All nodes have equal inflow and outflow and are balanced. Any discrepancies between inflows and outflows will be shown here."
        color="gray"
      />
    );
  }

  return (
    <ToolCard title="Some nodes are not balanced ⚠️" color="yellow">
      <Table.Root m="auto" pt={2}>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader />
            <Table.ColumnHeader textAlign="end">Total In</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="end">Total Out</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="end">Difference</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {diffNodes.map((node) => (
            <Table.Row key={node.name}>
              <Table.Cell>{node.name}</Table.Cell>
              <Table.Cell textAlign="end">
                <ImbalanceValue
                  formatAmount={formatAmount}
                  flows={node.flowsIn}
                  amount={node.totalIn ?? NaN}
                />
              </Table.Cell>
              <Table.Cell textAlign="end">
                <ImbalanceValue
                  formatAmount={formatAmount}
                  flows={node.flowsOut}
                  amount={node.totalOut ?? NaN}
                />
              </Table.Cell>
              <Table.Cell textAlign="end">
                {formatAmount((node.totalIn ?? NaN) - (node.totalOut ?? NaN))}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </ToolCard>
  );
};

export default NodeImbalances;
